import { useState } from 'react';
import { X, Check, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';

const fmt = (n, moneda = 'CRC') =>
  `${moneda === 'USD' ? '$' : '₡'}${(Number(n) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Quién pagó una compra de CONTADO (FE recibida o la compra de una FEC): la empresa (sale del banco) o
 * un empleado con su dinero, que se le reintegra en planilla. Con «asume la empresa» < 100 % (beneficio
 * parcial, p. ej. 25 % de un curso) solo esa parte queda como gasto de la empresa.
 */
export default function PagadoPorModal({ compraId, titulo, total, moneda, inicial, puedePasarACredito, onPasarACredito, onClose, onGuardado }) {
  const [pagadoPor, setPagadoPor] = useState(inicial?.pagadoPor || 'empresa');
  const [empleadoNombre, setEmpleadoNombre] = useState(inicial?.empleadoNombre || '');
  const [porcentaje, setPorcentaje] = useState(inicial?.porcentajeEmpresa ?? 100);
  // Monto que asume la empresa: ligado al % (se escribe uno y el otro se calcula)
  const r2 = (n) => Math.round(n * 100) / 100;
  const [montoEmpresa, setMontoEmpresa] = useState(String(r2((total || 0) * (inicial?.porcentajeEmpresa ?? 100) / 100)));
  const cambiarPct = (v) => { setPorcentaje(v); const n = Number(v); if (v !== '' && Number.isFinite(n)) setMontoEmpresa(String(r2((total || 0) * n / 100))); };
  const cambiarMonto = (v) => { setMontoEmpresa(v); const n = Number(v); if (v !== '' && Number.isFinite(n) && total > 0) setPorcentaje(String(r2(n / total * 100))); };
  const [guardando, setGuardando] = useState(false);

  const pct = Number(porcentaje);
  const pctOk = porcentaje !== '' && Number.isFinite(pct) && pct >= 0 && pct <= 100;
  const esEmpleado = pagadoPor === 'empleado';

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await gastosApi.pagadoPor(compraId, esEmpleado
        ? { pagadoPor: 'empleado', empleadoNombre: empleadoNombre.trim(), porcentajeEmpresa: pct }
        : { pagadoPor: 'empresa' });
      toast.success(r.data?.message || 'Guardado');
      onGuardado?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !guardando && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl border-t-4 border-emerald-500" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Pago de compra de contado</h3>
            <p className="text-sm text-gray-500 truncate">{titulo} · <strong className="text-gray-800">{fmt(total, moneda)}</strong></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Pagada por</p>
            {[
              { v: 'empresa', t: 'La empresa', d: 'Salió del banco de la empresa' },
              { v: 'empleado', t: 'Un empleado', d: 'La pagó con su dinero; se le reintegra en planilla' },
            ].map(o => (
              <label key={o.v} className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer ${pagadoPor === o.v ? 'border-emerald-500' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" className="mt-1" checked={pagadoPor === o.v} onChange={() => setPagadoPor(o.v)} />
                <span><span className="block text-sm font-medium text-gray-900">{o.t}</span><span className="block text-xs text-gray-500">{o.d}</span></span>
              </label>
            ))}
          </div>

          <div className={`space-y-3 ${esEmpleado ? '' : 'opacity-40 pointer-events-none'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
              <input type="text" value={empleadoNombre} maxLength={150} onChange={(e) => setEmpleadoNombre(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asume la empresa (%)</label>
                <input type="number" min="0" max="100" step="0.01" value={porcentaje} onChange={(e) => cambiarPct(e.target.value)}
                  className={`w-32 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 ${pctOk ? 'border-gray-300' : 'border-red-400'}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">o el monto ({moneda === 'USD' ? '$' : '₡'})</label>
                <input type="number" min="0" step="0.01" value={montoEmpresa} onChange={(e) => cambiarMonto(e.target.value)}
                  className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            {esEmpleado && pctOk && (
              <div className="text-sm border border-gray-200 rounded-lg divide-y">
                <div className="flex justify-between gap-3 px-3 py-1.5"><span className="text-gray-600">Gasto de la empresa (se reintegra)</span><strong className="whitespace-nowrap">{fmt(total * pct / 100, moneda)}</strong></div>
                {pct < 100 && <div className="flex justify-between gap-3 px-3 py-1.5"><span className="text-gray-600">Parte del empleado (no es gasto)</span><strong className="whitespace-nowrap">{fmt(total - total * pct / 100, moneda)}</strong></div>}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t">
          {onPasarACredito ? (
            <button onClick={onPasarACredito} disabled={guardando || !puedePasarACredito}
              title={puedePasarACredito ? '' : 'La condición ya fue corregida'}
              className="px-4 py-2 border-2 border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-40 text-sm">
              Pasar a crédito
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <button onClick={onClose} disabled={guardando} className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm">Cancelar</button>
            <button onClick={guardar} disabled={guardando || (esEmpleado && (!empleadoNombre.trim() || !pctOk))}
              className="flex items-center gap-2 px-4 py-2 border-2 border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 disabled:opacity-40 text-sm">
              {guardando ? <RefreshCw className="animate-spin" size={16} /> : <Check size={16} />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
